import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import "./index.css";

import logo from "./Logo.png";
import xIcon from "./Xicon.png";

export function App() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");

  const apiBaseUrl =
    (
      import.meta as ImportMeta & { env?: { PUBLIC_API_URL?: string } }
    ).env?.PUBLIC_API_URL?.replace(/\/$/, "") ??
    "https://kagan-waitlist-api.felipe-ba-moura.workers.dev";

  return (
    <div className="min-h-screen w-full">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-6 py-10">
        <header className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="pixel-frame">
              <img
                src={logo}
                alt="Court gavel logo"
                className="logo-pixel h-14 w-14"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">
                kagan project
              </p>
              <h1 className="pixel-title text-xl text-foreground">kagan</h1>
            </div>
          </div>
          <a
            className="inline-flex items-center justify-center transition"
            href="https://x.com/felipavav"
            target="_blank"
            rel="noreferrer"
          >
            <img src={xIcon} alt="X icon" className="logo-pixel h-16 w-16" />
          </a>
        </header>

        <main className="mt-10 flex flex-1 items-start">
          <section className="max-w-6xl">
            <div className="flex flex-wrap items-center gap-3">
              <span className="pixel-badge">on-chain rules</span>
              <span className="pixel-badge">transparent payouts</span>
              <span className="pixel-badge">built to last</span>
            </div>
            <h2 className="pixel-title mt-6 text-3xl sm:text-4xl lg:text-5xl">
              <span className="block">Backed by Code, Not Promises.</span>
              <span className="block">
                Fund Your Game With Rules You Can Verify.
              </span>
              <span className="block">
                Fans Invest → You Build → Payouts Are Enforced.
              </span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              This is a marketplace, not a mystery box. Ownership, royalties,
              and transfers are enforced on-chain so payments can’t be skipped
              or “changed later.” Build with real fans and real rules.
            </p>

            <form
              className="mt-8 flex flex-col gap-3 sm:flex-row"
              onSubmit={async (event) => {
                event.preventDefault();
                if (!email.trim()) {
                  setStatus("error");
                  setMessage("email is required");
                  return;
                }

                setStatus("loading");
                setMessage("");

                try {
                  const response = await fetch(`${apiBaseUrl}/emails`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ email: email.trim() }),
                  });

                  if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    setStatus("error");
                    setMessage(
                      typeof data.error === "string"
                        ? data.error
                        : "submission failed",
                    );
                    return;
                  }

                  setStatus("success");
                  setMessage("thanks for joining the court");
                  setEmail("");
                } catch (error) {
                  setStatus("error");
                  setMessage("network error, try again");
                }
              }}
            >
              <Input
                type="email"
                placeholder="you@terminal.io"
                className="h-11 border-border/60 bg-background/70 text-foreground placeholder:text-muted-foreground/70"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <Button
                className="pixel-button h-11 px-6 text-sm font-semibold uppercase tracking-[0.18em]"
                disabled={status === "loading"}
              >
                <span>{status === "loading" ? "sending" : "flip with us"}</span>
                {status === "loading" ? (
                  <span className="pixel-loader" aria-hidden="true" />
                ) : null}
              </Button>
            </form>
            <p className="mt-3 text-sm  tracking-[0.2em] text-muted-foreground">
              {status === "success" ? message : "no spam"}
            </p>
            {status === "error" ? (
              <p className="mt-2 text-sm tracking-[0.2em] text-red-400">
                {message}
              </p>
            ) : null}

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Card className="pixel-panel">
                <CardContent className="pt-6">
                  <p className="text-l  tracking-[0.3em] text-muted-foreground">
                    trust
                  </p>
                  <p className="mt-3 text-sm text-muted-foreground">
                    on-chain ownership and enforced payouts mean every sale is
                    verifiable. no backroom edits, no skipped royalties.
                  </p>
                </CardContent>
              </Card>
              <Card className="pixel-panel">
                <CardContent className="pt-6">
                  <p className="text-l  tracking-[0.3em] text-muted-foreground">
                    future
                  </p>
                  <p className="mt-3 text-sm text-muted-foreground ">
                    a marketplace where creators and fans both win, with rules
                    locked in code and payments that settle as promised.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        </main>

        <footer className="mt-10 flex flex-col gap-4 text-xs uppercase tracking-[0.3em] text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>kagan pixel gazette</span>
          <span>judge-court palette • terminal calm</span>
        </footer>
      </div>
    </div>
  );
}

export default App;
